import type { NavGroup, NavItem, SiteContent } from "./types";

/* The sidebar's collapsible groups as shipped: the kit's Casino, Promotions
   and Leaderboards. Each is the operator's to rename, reorder, remove or add
   to under Navigation, links included. The casino group is marked
   `categories`: the platform's casino categories follow its own links, so
   the operator owns the lobby link and the platform keeps the categories. */
export const CASINO_GROUP: NavGroup = {
  label: "Casino",
  icon: "spade",
  open: true,
  categories: true,
  items: [{ label: "Lobby", href: "/casino", icon: "gamepad" }],
};

export const PROMOTIONS_GROUP: NavGroup = {
  label: "Promotions",
  icon: "megaphone",
  items: [
    { label: "All Promotions", href: "/promotions", icon: "megaphone" },
    { label: "Welcome Offer", href: "/promotions", icon: "gift" },
  ],
};

export const LEADERBOARDS_GROUP: NavGroup = {
  label: "Leaderboards",
  icon: "trophy",
  items: [
    { label: "Weekly Race", href: "/leaderboards", icon: "trophy" },
    { label: "Hall of Fame", href: "/leaderboards", icon: "star" },
  ],
};

export const DEFAULT_NAV_GROUPS: NavGroup[] = [CASINO_GROUP, PROMOTIONS_GROUP, LEADERBOARDS_GROUP];

const copyGroup = (group: NavGroup, items?: unknown): NavGroup => ({
  ...group,
  items: Array.isArray(items) ? (items as NavItem[]) : group.items.map((item) => ({ ...item })),
});

/* Navigation stored before the groups were a list, when `promos` and
   `boards` were two fixed groups. They become the Promotions and Leaderboards
   groups, behind the shipped casino group, so an operator's edited links
   survive the change; a stored nav that already has groups only sheds the
   stale keys. Anything that is not a nav object passes through untouched. */
export function upgradeNav(value: unknown): unknown {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return value;

  const nav = value as Record<string, unknown>;
  if (!("promos" in nav) && !("boards" in nav)) return value;

  const { promos, boards, ...rest } = nav;
  if (Array.isArray(rest.groups)) return rest;

  return {
    ...rest,
    groups: [copyGroup(CASINO_GROUP), copyGroup(PROMOTIONS_GROUP, promos), copyGroup(LEADERBOARDS_GROUP, boards)],
  };
}

/* The groups as the rail draws them: the platform's categories after the
   links of every group that asked for them. */
export function navGroups(nav: SiteContent["nav"], categories: NavItem[]): NavGroup[] {
  return nav.groups.map((group) => (group.categories ? { ...group, items: [...group.items, ...categories] } : group));
}
